import {
  Autocomplete as AutocompleteComponent,
  AutocompleteProps,
  a11yHelpers,
} from '@flixbus/honeycomb-react';
import React, { useMemo, useRef, useState } from 'react';
import { TextWithEllipsis } from 'app/components/ui/TextWithEllipsis/TextWithEllipsis';
import { Icon } from 'app/icons';
import * as styles from './Autocomplete.scss';
import { requestAutocomplete } from 'app/store/thunk/location/requestAutocompleteThunk';
import { useResults } from 'app/hooks/useResults';
import { IconComponent } from '@flixbus/honeycomb-icons-react/src/components/icons/types';
import { GroupTitle } from '../GroupTitle/GroupTitle';
import { useAutocomplete } from '../../../hooks/useAutocomplete';
import { useTripLocation } from 'app/hooks/useTripLocation';
import {
  hideExplorationMapLinkSelector,
  openedOverlaySelector,
  selectedLocationsSelector,
} from 'app/store/selectors';
import { emitFieldOpenedEvent } from 'app/events';
import { FIELD_ARRIVAL_CITY, FIELD_DEPARTURE_CITY } from 'app/storage/fieldNames';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { useTranslations } from 'app/hooks/useTranslations';
import { closeElement } from 'app/functions/general/closeElement';
import { useAppDispatch, useAppSelector } from 'app/hooks/appHooks';
import { Option } from '../Option/Option';
import { RemoveButton } from './RemoveButton';
import { MapLink } from '../MapLink/MapLink';
import { emitSearchMaskMapEvent } from 'app/events/emitSearchMaskMapEvent';
import { redirectToUrl } from 'app/functions';
import debounce from 'lodash/debounce';

export type AutocompleteTypes = {
  direction: 'from' | 'to';
  afterSelectAction?: () => void;
  icon?: IconComponent;
  label?: string;
  extraClasses?: string;
  tabTrapAction?: (e: React.KeyboardEvent) => void;
};

const GLOBAL_MAP_LINK = 'https://global.flixbus.com/bus-routes';

export const Autocomplete = ({
  direction,
  afterSelectAction,
  icon,
  label,
  extraClasses,
  tabTrapAction,
}: AutocompleteTypes & Partial<AutocompleteProps>) => {
  const openedOverlayName = useAppSelector(openedOverlaySelector)?.name;
  const isAlwaysExpanded: boolean = openedOverlayName === `TripLocationPanel-${direction}`;
  const { message, value, setValue } = useTripLocation(direction);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const hideExplorationMapLink = useAppSelector(hideExplorationMapLinkSelector);
  const departureCityId = useAppSelector(selectedLocationsSelector).from.uuid || '';

  const {
    type: resultsType,
    options,
    citiesById,
  } = useResults({ direction, hasQuery: !!value, isFocused });

  const {
    getGroupResultsAmount,
    getTranslation,
    getMapLink,
    getExploreMap,
    getPopularPlaces,
    getRecentSearches,
    getDepartureAriaLabel,
    getReturnAriaLabel,
  } = useTranslations(
    {
      getGroupResultsAmount: a11yLabelsKeys.locationInputs.groupResultsAmount,
      getMapLink: 'search_mask.explore_map.link_href',
      getExploreMap: 'search_mask.label.explore_the_map',
      getPopularPlaces: 'search_mask.label.popular_places',
      getRecentSearches: 'search_mask.label.recent_searches',
      getDepartureAriaLabel: a11yLabelsKeys.locationInputs.departure,
      getReturnAriaLabel: a11yLabelsKeys.locationInputs.arrival,
    },
    true,
  );

  const { onSelect, selected, wrapperRef } = useAutocomplete({
    direction,
    setValue,
    setIsFocused,
    isAlwaysExpanded,
    citiesById,
    afterSelectAction,
    resultsType,
    value,
  });

  const visibleMessage = message && resultsType === 'autocomplete' ? message : null;

  const onMapClick = () => {
    const params = departureCityId && direction === 'to' ? `?departureCity=${departureCityId}` : '';
    const href = `${getMapLink() || GLOBAL_MAP_LINK}${params}`;
    emitSearchMaskMapEvent();
    return redirectToUrl(href);
  };

  const renderGroupName = ({ group }) => {
    const groupTranslation = {
      popular: getPopularPlaces(),
      recent: getRecentSearches(),
      default: getTranslation(`search_mask.continent.${group}`),
    };

    return <GroupTitle group={group} title={groupTranslation[group] || groupTranslation.default} />;
  };

  const renderRemoveButton = (value: string | number) => {
    if (resultsType === 'recent' && options[0]?.options) {
      const cityData = value ? citiesById[value] : undefined;
      return (
        <RemoveButton
          cityName={cityData?.name || ''}
          cityId={cityData?.id || ''}
          direction={direction}
        />
      );
    }
  };

  const fetchResults = useMemo(
    () =>
      debounce((value) => {
        if (value) {
          dispatch(
            requestAutocomplete({
              query: value,
              direction,
              sendDepartureCityId: direction === 'to',
            }),
          );
        }
      }, 300),
    [],
  );

  const handleInput = ({ currentTarget: { value } }: React.SyntheticEvent<HTMLInputElement>) => {
    if (!isFocused) {
      setIsFocused(true);
    }

    setValue(value);
    fetchResults(value);
  };

  return (
    <div ref={wrapperRef}>
      <AutocompleteComponent
        role="none"
        data-e2e={`autocomplete-component-${direction}`}
        options={options}
        inputValue={value}
        // need for correct work
        onDebounce={() => {}}
        // called when remove all input
        onChange={(selected) => (selected[0] ? onSelect(selected[0]) : null)}
        extraClasses={extraClasses}
        onToggle={(expanded) => {
          if (expanded || isAlwaysExpanded) return;

          closeElement(direction === 'from' ? FIELD_DEPARTURE_CITY : FIELD_ARRIVAL_CITY, undefined);
        }}
        comboboxProps={{
          id: `searchInput-${direction}`,
          label: label ? <TextWithEllipsis text={label} /> : '',
          'aria-label': direction === 'from' ? getDepartureAriaLabel() : getReturnAriaLabel(),
          iconLeft: icon && <Icon aria-hidden appearance="primary" InlineIcon={icon} />,
          extraClasses: styles.input,
          inputFieldRef: inputRef,
          onFocus: () => {
            if (!isAlwaysExpanded) {
              emitFieldOpenedEvent(
                direction === 'from' ? FIELD_DEPARTURE_CITY : FIELD_ARRIVAL_CITY,
              );
            }
            setValue('');
            setIsFocused(true);
          },
          onInput: handleInput,
          placeholder: selected.stationName || selected.name,
          onKeyDown: tabTrapAction,
          info: isFocused ? (
            <span className={a11yHelpers().srOnly} aria-live="polite">
              {getGroupResultsAmount({}, options.length)}
            </span>
          ) : null,
        }}
        expanded={isFocused}
        listBoxProps={{
          role: 'presentation',
          optionsToDisplay: 10,
          extraClasses: styles.options,
          renderGroupName,
          actionButtonProps: {
            children: !hideExplorationMapLink ? (
              <>
                <MapLink
                  title={getTranslation(
                    `search_mask.label.${direction === 'from' ? 'where_to_start' : 'where_to_go'}`,
                  )}
                  text={getExploreMap()}
                />
              </>
            ) : null,
            'aria-label': getExploreMap(),
            extraClasses: styles.mapLink,
            onClick: onMapClick,
          },
          optionProps: (option) => ({
            renderOption: () => (
              <Option
                value={value}
                resultType={resultsType}
                item={option}
                citiesById={citiesById}
                direction={direction}
              />
            ),
            renderWidget: () => renderRemoveButton(option.value),
          }),
        }}
      />
      {visibleMessage && isFocused && <div className={styles.errorMessage}>{visibleMessage}</div>}
    </div>
  );
};
